'use client'

import { useState, useEffect, useContext, useRef } from 'react'
import { Heading } from '@/components/Heading'
import Input from '@/components/Input'
import AddressFinderInput from '@/components/AddressFinderInput'
import { Select } from '@/components/Select'
import { Checkbox } from '@/components/Checkbox'
import { Box } from '@/components/Box'
import { Button } from '@/components/Button'
import { Question } from '@/components/Question'
import {
  selectTitleOptions,
  loanTermOptions,
  paymentFrequencyOptions, 
  martialStatusOptions,
  dependantsOptions,
  drivingLicenceOptions,
  listOfCountries,
  residenceTypeOptions
} from '@/data/selectOptions'
import { ROUTES } from '@/config/routes'
import useFormContext from '../../../context/useFormContext'
import { useForm } from 'react-hook-form'
import { generateModelYearOptions, generateAddressYearsOptions, generateMonthOptions } from '@/utils/dateUtils'
import PurchaseItems from '@/pages/personal/components/PurchaseItems'
import { Address } from 'react-addressfinder'
import { ISO_3166_MAPPER } from '@/data/ISO_3166_MAPPER'
import { subYears, format } from 'date-fns'
import { Header } from '@/components/HeaderPersonal'
import { emailRegex, defaultMinPersonalLoanAmount, mobileNumberRegex } from '@/utils/constant'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { DealerContext } from '@/context/DealerContext'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { useFormSaveForLater } from '@/hooks/useFormSaveForLater'
import { preparePayload } from '@/services/saveService'
import { scrollToFirstError } from '@/utils/helper'
import { validateMoney } from '@/utils/validation'

const modelYearOptions = generateModelYearOptions(true)
const addressYearsOptions = generateAddressYearsOptions()
const addressMonthOptions = generateMonthOptions(true)

// Also a person can't be older than 100 years, so today minus 100 years
// A person need to be at least 18 years old, so today minus 18 years
// TODO: Refactor with other places in the code, this can be found inside YourDetails.tsx as well
const minDateOfBirth = format(subYears(new Date(), 100), 'yyyy-MM-dd')
const maxDateOfBirth = format(subYears(new Date(), 18), 'yyyy-MM-dd')

function AboutYou() {
  const navigate = usePersistedNavigation()
  const { state, dispatch } = useFormContext()
  const dealerContext = useContext(DealerContext)
  const [showPurchaseItemAddWarning, setShowPurchaseItemAddWarning] = useState(false)
  const [countryOfCitizenship, setCountryOfCitizenship] = useState('')
  const [countryOfCoBorrowerCitizenship, setCountryOfCoBorrowerCitizenship] = useState('')
  const [completedSelectingPurchaseItem, setCompletedSelectingPurchaseItem] = useState(state.personal.aboutYou.purchaseItems.itemType !== '')
  const [numberOfAdditionalCitizenships, setNumberOfAdditionalCitizenships] = useState(state.personal.aboutYou.countryOfCitizenship ? state.personal.aboutYou.countryOfCitizenship.split(',').length - 1 : 0)
  const [numberOfCoBorrowerAdditionalCitizenships, setNumberOfCoBorrowerAdditionalCitizenships] = useState(
    state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountryOfCitizenship 
      ? state.personal.aboutYou.coBorrowerDetails.coBorrowerCountryOfCitizenship.split(',').length - 1 
      : 0
  )
  const orderOfByingOptions = ['MV', 'RM', 'TB', 'MH']
  const buyingOptions = dealerContext?.dealerInformation?.buyingOptions || []
  const firstOption = orderOfByingOptions.find(option => buyingOptions.includes(option)) || orderOfByingOptions[0]
  const [selectedPurchaseItem, setSelectedPurchaseItem] = useState(state.personal.aboutYou.purchaseItems.itemType || firstOption)

  // Create separate form instances
  const purchaseItemForm = useForm({
    defaultValues: {
      year: state.personal.aboutYou.purchaseItems.year,
      purchasePrice: state.personal.aboutYou.purchaseItems.purchasePrice,
      cashDeposit: state.personal.aboutYou.purchaseItems.cashDeposit,
      make: state.personal.aboutYou.purchaseItems.make,
      model: state.personal.aboutYou.purchaseItems.model,
      loanTerm: state.personal.aboutYou.purchaseItems.loanTerm,
      repaymentFrequency: state.personal.aboutYou.purchaseItems.repaymentFrequency,
    }
  })

  const aboutYouForm = useForm({
    defaultValues: {
      // All other fields
      isJoint: state.personal.aboutYou.isJoint,
      maritalStatus: state.personal.aboutYou.maritalStatus,
      dependantsNumber: state.personal.aboutYou.dependantsNumber,
      driverLicenceType: state.personal.aboutYou.driverLicenceType,
      driverLicenceNumber: state.personal.aboutYou.driverLicenceNumber,
      driverLicenceVersion: state.personal.aboutYou.driverLicenceVersion,
      nzResident: state.personal.aboutYou.nzResident,
      countryOfBirth: state.personal.aboutYou.countryOfBirth,
      countryOfCitizenship: state.personal.aboutYou.countryOfCitizenship || '',
      address: state.personal.aboutYou.address,
      city: state.personal.aboutYou.city,
      ZIP: state.personal.aboutYou.ZIP,
      country: state.personal.aboutYou.country,
      residenceType: state.personal.aboutYou.residenceType,
      timeAtAddressYears: state.personal.aboutYou.timeAtAddressYears,
      timeAtAddressMonths: state.personal.aboutYou.timeAtAddressMonths,
      reuseForPostalAddress: state.personal.aboutYou.reuseForPostalAddress,
      previousAddress: state.personal.aboutYou.previousAddress,
      previousCity: state.personal.aboutYou.previousCity,
      previousZIP: state.personal.aboutYou.previousZIP,
      previousCountry: state.personal.aboutYou.previousCountry,
      previousTimeAtAddressYears: state.personal.aboutYou.previousTimeAtAddressYears,
      previousTimeAtAddressMonths: state.personal.aboutYou.previousTimeAtAddressMonths,
      postAddress: state.personal.aboutYou.postAddress,
      postCity: state.personal.aboutYou.postCity,
      postZIP: state.personal.aboutYou.postZIP,
      postCountry: state.personal.aboutYou.postCountry,

      // Co-borrower fields
      coBorrowerTitle: state.personal.aboutYou.coBorrowerDetails?.coBorrowerTitle ?? null,
      coBorrowerFirstName: state.personal.aboutYou.coBorrowerDetails?.coBorrowerFirstName ?? null,
      coBorrowerMiddleName: state.personal.aboutYou.coBorrowerDetails?.coBorrowerMiddleName ?? null,
      coBorrowerLastName: state.personal.aboutYou.coBorrowerDetails?.coBorrowerLastName ?? null,
      coBorrowerPhoneNumber: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPhoneNumber ?? null,
      coBorrowerEmail: state.personal.aboutYou.coBorrowerDetails?.coBorrowerEmail ?? null,
      coBorrowerDOB: state.personal.aboutYou.coBorrowerDetails?.coBorrowerDOB ?? null,
      coBorrowerMaritalStatus: state.personal.aboutYou.coBorrowerDetails?.coBorrowerMaritalStatus ?? null,
      coBorrowerDependantsNumber: state.personal.aboutYou.coBorrowerDetails?.coBorrowerDependantsNumber ?? null,
      coBorrowerNZResident: state.personal.aboutYou.coBorrowerDetails?.coBorrowerNZResident ?? null,
      coBorrowerCountryOfBirth: state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountryOfBirth ?? null,
      coBorrowerCountryOfCitizenship: state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountryOfCitizenship ?? '',
      coBorrowerDriverLicenceType: state.personal.aboutYou.coBorrowerDetails?.coBorrowerDriverLicenceType ?? null,
      coBorrowerDriverLicenceNumber: state.personal.aboutYou.coBorrowerDetails?.coBorrowerDriverLicenceNumber ?? null,
      coBorrowerDriverLicenceVersion: state.personal.aboutYou.coBorrowerDetails?.coBorrowerDriverLicenceVersion ?? null,
      isCoBorrowerLivingTogether: state.personal.aboutYou.coBorrowerDetails?.isCoBorrowerLivingTogether ?? null,
      coBorrowerAddress: state.personal.aboutYou.coBorrowerDetails?.coBorrowerAddress ?? null,
      coBorrowerCity: state.personal.aboutYou.coBorrowerDetails?.coBorrowerCity ?? null,
      coBorrowerZIP: state.personal.aboutYou.coBorrowerDetails?.coBorrowerZIP ?? null,
      coBorrowerCountry: state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountry ?? null,
      coBorrowerResidenceType: state.personal.aboutYou.coBorrowerDetails?.coBorrowerResidenceType ?? null,
      coBorrowerReuseForPostalAddress: state.personal.aboutYou.coBorrowerDetails?.coBorrowerReuseForPostalAddress ?? null,
      coBorrowerTimeAtAddressYears: state.personal.aboutYou.coBorrowerDetails?.coBorrowerTimeAtAddressYears ?? null,
      coBorrowerTimeAtAddressMonths: state.personal.aboutYou.coBorrowerDetails?.coBorrowerTimeAtAddressMonths ?? null,
      coBorrowerPreviousAddress: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousAddress ?? null,
      coBorrowerPreviousCity: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousCity ?? null,
      coBorrowerPreviousZIP: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousZIP ?? null,
      coBorrowerPreviousCountry: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousCountry ?? null,
      coBorrowerPreviousTimeAtAddressYears: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousTimeAtAddressYears ?? null,
      coBorrowerPreviousTimeAtAddressMonths: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousTimeAtAddressMonths ?? null,
      coBorrowerPostAddress: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostAddress ?? null,
      coBorrowerPostCity: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostCity ?? null,
      coBorrowerPostZIP: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostZIP ?? null,
      coBorrowerPostCountry: state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostCountry ?? null,
    }
  })

  const doesUserHaveDrivingLicence = aboutYouForm.watch('driverLicenceType') !== 'No licence'
  const doesCoBorrowerHaveDrivingLicence = aboutYouForm.watch('coBorrowerDriverLicenceType') !== 'No licence'
  const isJointApplication = aboutYouForm.watch('isJoint') === true
  const showYourPostalAddress = !aboutYouForm.watch('reuseForPostalAddress')
  const showPreviousAddress = aboutYouForm.watch('timeAtAddressYears') < 3
  const showCoBorrowerAddress = aboutYouForm.watch('isCoBorrowerLivingTogether') !== true
  const showCoBorrowerPostalAddress = !aboutYouForm.watch('coBorrowerReuseForPostalAddress')
  const showCoBorrowerPreviousAddress = aboutYouForm.watch('coBorrowerTimeAtAddressYears') < 3
  
  // We want to track state in ref for use in the triggerSave function which is passed as a callback on the mount
  const stateRef = useRef(state)
  useEffect(() => {
    console.log('About you state changed:', state.personal.aboutYou)
    stateRef.current = state 
  }, [state])

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => {
    aboutYouForm.handleSubmit(() => {})()
    // A small hack, so if user triggers validation, it will actually scroll to the top of the page, instead of
    // jumping to the main form errors
    window.setTimeout(() => purchaseItemForm.handleSubmit(() => {})(), 0)
  }

  // Register the validation trigger when the component mounts
  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  /* Handle saving for later */
  const { registerSaveCallback, unregisterSaveCallback } = useFormSaveForLater()
  const triggerSave = () => {
    const aboutYouData = aboutYouForm.getValues()
    const data = bindAboutYouData(aboutYouData)

    dispatchFormData(data)
    handleFormSave(preparePayload(data, 'personal', 'aboutYou'))
  }

  // Register the save form trigger when the component mounts
  useEffect(() => {
    registerSaveCallback(triggerSave)
    return () => unregisterSaveCallback()
  }, [registerSaveCallback, unregisterSaveCallback])
  /* End - Handle saving for later */

  const { handleFormSave } = useFormSubmission({
    onSuccess: (data) => {
      console.log('Form submission successful:', data)
    },
    onError: (error) => {
      console.error('Error during form save:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_PERSONAL',
      step: 'aboutYou',
      payload: data.personal.aboutYou
    })
  }

  const onPurchaseItemSubmit = (data: any) => {
    console.log('Purchase item data:', data)
    setCompletedSelectingPurchaseItem(true)
    setShowPurchaseItemAddWarning(false)
    dispatch({
      type: 'UPDATE_PERSONAL',
      step: 'aboutYou',
      payload: { 
        ...state.personal.aboutYou,
        purchaseItems: {
          itemType: selectedPurchaseItem,
          year: data.year,
          purchasePrice: data.purchasePrice,
          cashDeposit: data.cashDeposit === '' ? null : data.cashDeposit,
          make: data.make,
          model: data.model,
          loanTerm: data.loanTerm,
          repaymentFrequency: data.repaymentFrequency
        }
      }
    })
  }

  const bindAboutYouData = (data: any) => {
    const currentState = stateRef.current
    const purchaseItemsData = purchaseItemForm.getValues()

    return {
      ...currentState,
      business: null,
      personal: {
        ...currentState.personal,
        aboutYou: {
          ...currentState.personal.aboutYou,
          purchaseItems: {
            itemType: selectedPurchaseItem,
            ...purchaseItemsData,
            cashDeposit: purchaseItemsData.cashDeposit === '' ? null : purchaseItemsData.cashDeposit
          },
          isJoint: data.isJoint === '' ? false : data.isJoint,
          maritalStatus: data.maritalStatus,
          dependantsNumber: data.dependantsNumber === '' ? 0 : data.dependantsNumber,
          driverLicenceType: data.driverLicenceType,
          driverLicenceNumber: data.driverLicenceNumber,
          driverLicenceVersion: data.driverLicenceVersion,
          nzResident: data.nzResident === '' ? false : data.nzResident,
          countryOfBirth: data.countryOfBirth,
          countryOfCitizenship: data.countryOfCitizenship,
          address: data.address,
          city: data.city,
          ZIP: data.ZIP,
          country: data.country,
          residenceType: data.residenceType,
          timeAtAddressYears: data.timeAtAddressYears,
          timeAtAddressMonths: data.timeAtAddressMonths,
          reuseForPostalAddress: data.reuseForPostalAddress,
          previousAddress: data.previousAddress,
          previousCity: data.previousCity,
          previousZIP: data.previousZIP,
          previousCountry: data.previousCountry,
          previousTimeAtAddressYears: data.previousTimeAtAddressYears,
          previousTimeAtAddressMonths: data.previousTimeAtAddressMonths,
          postAddress: data.postAddress,
          postCity: data.postCity,
          postZIP: data.postZIP,
          postCountry: data.postCountry,
          coBorrowerDetails: {
            coBorrowerTitle: data.coBorrowerTitle,
            coBorrowerFirstName: data.coBorrowerFirstName,
            coBorrowerMiddleName: data.coBorrowerMiddleName,
            coBorrowerLastName: data.coBorrowerLastName,
            coBorrowerPhoneNumber: data.coBorrowerPhoneNumber,
            coBorrowerEmail: data.coBorrowerEmail,
            coBorrowerDOB: data.coBorrowerDOB,
            coBorrowerMaritalStatus: data.coBorrowerMaritalStatus,
            coBorrowerDependantsNumber: data.coBorrowerDependantsNumber === '' ? 0 : data.coBorrowerDependantsNumber,
            coBorrowerNZResident: data.coBorrowerNZResident === '' ? false : data.coBorrowerNZResident,
            coBorrowerCountryOfBirth: data.coBorrowerCountryOfBirth,
            coBorrowerCountryOfCitizenship: data.coBorrowerCountryOfCitizenship,
            coBorrowerDriverLicenceType: data.coBorrowerDriverLicenceType,
            coBorrowerDriverLicenceNumber: data.coBorrowerDriverLicenceNumber,
            coBorrowerDriverLicenceVersion: data.coBorrowerDriverLicenceVersion,
            coBorrowerAddress: data.coBorrowerAddress,
            coBorrowerCity: data.coBorrowerCity,
            coBorrowerZIP: data.coBorrowerZIP,
            coBorrowerCountry: data.coBorrowerCountry,
            coBorrowerResidenceType: data.coBorrowerResidenceType,
            coBorrowerReuseForPostalAddress: data.coBorrowerReuseForPostalAddress,
            coBorrowerTimeAtAddressYears: data.coBorrowerTimeAtAddressYears,
            coBorrowerTimeAtAddressMonths: data.coBorrowerTimeAtAddressMonths,
            coBorrowerPreviousAddress: data.coBorrowerPreviousAddress,
            coBorrowerPreviousCity: data.coBorrowerPreviousCity,
            coBorrowerPreviousZIP: data.coBorrowerPreviousZIP,
            coBorrowerPreviousCountry: data.coBorrowerPreviousCountry,
            coBorrowerPreviousTimeAtAddressYears: data.coBorrowerPreviousTimeAtAddressYears,
            coBorrowerPreviousTimeAtAddressMonths: data.coBorrowerPreviousTimeAtAddressMonths,
            coBorrowerPostAddress: data.coBorrowerPostAddress,
            coBorrowerPostCity: data.coBorrowerPostCity,
            coBorrowerPostZIP: data.coBorrowerPostZIP,
            coBorrowerPostCountry: data.coBorrowerPostCountry,
            isCoBorrowerLivingTogether: data.isCoBorrowerLivingTogether
          }
        }
      }
    }
  }

  const onAboutYouSubmit = async (data: any) => {
    // First validate the purchase item form
    const isPurchaseItemValid = await purchaseItemForm.trigger()
    if (!isPurchaseItemValid) {
      // If purchase item form is not valid, scroll to the top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    if (!completedSelectingPurchaseItem) {
      setShowPurchaseItemAddWarning(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    data = bindAboutYouData(data)
    dispatchFormData(data)
    handleFormSave(preparePayload(data, 'personal', 'aboutYou'), ROUTES.PERSONAL.INCOME.path)
  }

  // Register all custom input components for purchase item form
  useEffect(() => {
    purchaseItemForm.register('loanTerm', { required: 'Loan term is required' })
    purchaseItemForm.register('repaymentFrequency', { required: 'Repayment frequency is required' })
    purchaseItemForm.register('year', { required: 'Model year is required' })

    // Only register make and model custom select if dealer information is available
    if (dealerContext?.dealerInformation?.make && dealerContext.dealerInformation.make.length > 0) {
      purchaseItemForm.register('make')
    }
    if (dealerContext?.dealerInformation?.model && dealerContext.dealerInformation.model.length > 0) {
      purchaseItemForm.register('model')
    }
  }, [dealerContext?.dealerInformation])

  // Register all custom input components for about you form
  useEffect(() => {
    aboutYouForm.register('isJoint', { 
      validate: (value) => value === true || value === false || 'Application type is required'
    })
    aboutYouForm.register('maritalStatus', { required: 'Marital status is required' })
    aboutYouForm.register('dependantsNumber')
    aboutYouForm.register('driverLicenceType', { required: 'Driver licence type is required' })
    aboutYouForm.register('nzResident', {
      validate: (value) => value === true || value === false || 'This field is required'
    })
    aboutYouForm.register('countryOfBirth', { required: 'Country of birth is required' })
    aboutYouForm.register('countryOfCitizenship', { required: 'Country of citizenship is required' })
    aboutYouForm.register('address', { required: 'Address is required' })
    aboutYouForm.register('country', { required: 'Country is required' })
    aboutYouForm.register('residenceType', { required: 'Residence type is required' })
    aboutYouForm.register('timeAtAddressYears')
    aboutYouForm.register('timeAtAddressMonths')
    aboutYouForm.register('reuseForPostalAddress')
  }, [])

  // Handle conditional registration of fields based on their conditions
  useEffect(() => {
    const isJoint = aboutYouForm.watch('isJoint') === true
    const reuseForPostalAddress = aboutYouForm.watch('reuseForPostalAddress')
    const timeAtAddressYears = Number(aboutYouForm.watch('timeAtAddressYears'))
    const coBorrowerReuseForPostalAddress = aboutYouForm.watch('coBorrowerReuseForPostalAddress')
    const coBorrowerTimeAtAddressYears = Number(aboutYouForm.watch('coBorrowerTimeAtAddressYears'))
    
    // Handle postal address fields
    if (!reuseForPostalAddress) {
      aboutYouForm.register('postAddress', { required: 'Address is required' })
      aboutYouForm.register('postCountry', { required: 'Country is required' })
    } else {
      aboutYouForm.unregister('postAddress')
      aboutYouForm.unregister('postCountry')
    }

    // Handle previous address fields
    if (timeAtAddressYears < 3) {
      aboutYouForm.register('previousAddress', { required: 'Address is required' })
      aboutYouForm.register('previousCountry', { required: 'Country is required' })
    } else {
      aboutYouForm.unregister('previousAddress')
      aboutYouForm.unregister('previousCountry')
    }

    // Handle co-borrower fields
    if (isJoint) {
      // Register co-borrower fields when it's a joint application
      aboutYouForm.register('coBorrowerTitle', { required: 'Title is required' })
      aboutYouForm.register('coBorrowerMaritalStatus', { required: 'Marital status is required' })
      aboutYouForm.register('coBorrowerDependantsNumber')
      aboutYouForm.register('coBorrowerDriverLicenceType', { required: 'Driver licence type is required' })
      aboutYouForm.register('coBorrowerNZResident', { 
        validate: (value) => value === true || value === false || 'This field is required'
      })
      aboutYouForm.register('coBorrowerCountryOfBirth', { required: 'Country of birth is required' })
      aboutYouForm.register('coBorrowerCountryOfCitizenship', { required: 'Country of citizenship is required' })
      aboutYouForm.register('isCoBorrowerLivingTogether', { 
        validate: (value) => value === true || value === false || 'This field is required'
      })

      if (showCoBorrowerAddress) {
        aboutYouForm.register('coBorrowerAddress', { required: 'Address is required' })
        aboutYouForm.register('coBorrowerCountry', { required: 'Country is required' })
        aboutYouForm.register('coBorrowerResidenceType', { required: 'Residence type is required' })
        aboutYouForm.register('coBorrowerTimeAtAddressYears')
        aboutYouForm.register('coBorrowerTimeAtAddressMonths')

        if (coBorrowerTimeAtAddressYears < 3) {
          aboutYouForm.register('coBorrowerPreviousAddress', { required: 'Address is required' })
          aboutYouForm.register('coBorrowerPreviousCountry', { required: 'Country is required' })
          aboutYouForm.register('coBorrowerPreviousTimeAtAddressYears')
          aboutYouForm.register('coBorrowerPreviousTimeAtAddressMonths')
        } else {
          aboutYouForm.unregister('coBorrowerPreviousAddress')
          aboutYouForm.unregister('coBorrowerPreviousCountry')
          aboutYouForm.unregister('coBorrowerPreviousTimeAtAddressYears')
          aboutYouForm.unregister('coBorrowerPreviousTimeAtAddressMonths')
        }

        // Register only if the user is not reusing the postal address
        if (!coBorrowerReuseForPostalAddress) {
          aboutYouForm.register('coBorrowerPostAddress', { required: 'Address is required' })
          aboutYouForm.register('coBorrowerPostCountry', { required: 'Country is required' })
        } else {
          aboutYouForm.unregister('coBorrowerPostAddress')
          aboutYouForm.unregister('coBorrowerPostCountry')
        }
      } else {
        aboutYouForm.unregister('coBorrowerAddress')
        aboutYouForm.unregister('coBorrowerCountry')
        aboutYouForm.unregister('coBorrowerResidenceType')
        aboutYouForm.unregister('coBorrowerTimeAtAddressYears')
        aboutYouForm.unregister('coBorrowerTimeAtAddressMonths')
        aboutYouForm.unregister('coBorrowerPreviousAddress')
        aboutYouForm.unregister('coBorrowerPreviousCountry')
        aboutYouForm.unregister('coBorrowerPreviousTimeAtAddressYears')
        aboutYouForm.unregister('coBorrowerPreviousTimeAtAddressMonths')
        aboutYouForm.unregister('coBorrowerPostAddress')
        aboutYouForm.unregister('coBorrowerPostCountry')
      }
    } else {
      // Unregister co-borrower fields when it's a single application
      aboutYouForm.unregister('coBorrowerTitle')
      aboutYouForm.unregister('coBorrowerMaritalStatus')
      aboutYouForm.unregister('coBorrowerDependantsNumber')
      aboutYouForm.unregister('coBorrowerDriverLicenceType')
      aboutYouForm.unregister('coBorrowerNZResident')
      aboutYouForm.unregister('coBorrowerCountryOfBirth')
      aboutYouForm.unregister('coBorrowerCountryOfCitizenship')
      aboutYouForm.unregister('isCoBorrowerLivingTogether')
      aboutYouForm.unregister('coBorrowerAddress')
      aboutYouForm.unregister('coBorrowerCountry')
      aboutYouForm.unregister('coBorrowerResidenceType')
      aboutYouForm.unregister('coBorrowerTimeAtAddressYears')
      aboutYouForm.unregister('coBorrowerTimeAtAddressMonths')
      aboutYouForm.unregister('coBorrowerPreviousAddress')
      aboutYouForm.unregister('coBorrowerPreviousCountry')
      aboutYouForm.unregister('coBorrowerPreviousTimeAtAddressYears')
      aboutYouForm.unregister('coBorrowerPreviousTimeAtAddressMonths')
      aboutYouForm.unregister('coBorrowerPostAddress')
      aboutYouForm.unregister('coBorrowerPostCountry')
    }
  }, [
    aboutYouForm.watch('isJoint'),
    aboutYouForm.watch('reuseForPostalAddress'),
    aboutYouForm.watch('timeAtAddressYears'),
    aboutYouForm.watch('coBorrowerReuseForPostalAddress'),
    aboutYouForm.watch('coBorrowerTimeAtAddressYears'),
    showCoBorrowerAddress
  ])

  // Handle our custom input components
  const handleCustomInputChange = (fieldName: string, value: string | boolean) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    aboutYouForm.setValue(fieldName, value)
    
    // For some reason AddressFinderInput inputs the isSubmitted is always false, so we need to trigger the validation manually
    // Ideally we will investigate it further and fix it
    if (fieldName === 'address') {
      aboutYouForm.trigger(fieldName)
    }
    
    if (aboutYouForm.formState.isSubmitted) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYouForm.trigger(fieldName)

      if (fieldName === 'driverLicenceType') {
        if (value === 'No licence') {
          aboutYouForm.setValue('driverLicenceNumber', '')
          aboutYouForm.setValue('driverLicenceVersion', '')
          aboutYouForm.clearErrors('driverLicenceNumber')
          aboutYouForm.clearErrors('driverLicenceVersion')
        }
      }

      if (fieldName === 'coBorrowerDriverLicenceType') {
        if (value === 'No licence') {
          aboutYouForm.setValue('coBorrowerDriverLicenceNumber', '')
          aboutYouForm.setValue('coBorrowerDriverLicenceVersion', '')
          aboutYouForm.clearErrors('coBorrowerDriverLicenceNumber')
          aboutYouForm.clearErrors('coBorrowerDriverLicenceVersion')
        }
      }
    }
  }

  // Handle purchase item form custom input changes
  const handlePurchaseItemInputChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    purchaseItemForm.setValue(fieldName, value)
    
    if (purchaseItemForm.formState.isSubmitted) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      purchaseItemForm.trigger(fieldName)
    }
  }

  const updateAddressFields = (prefix: string, address: Address, isCoBorrower = false) => {
    const cityField = prefix ? `${prefix}City` : 'city'
    const zipField = prefix ? `${prefix}ZIP` : 'ZIP'
    const countryField = prefix ? `${prefix}Country` : 'country'
  
    if (address.city) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYouForm.setValue(cityField, address.city); aboutYouForm.clearErrors(cityField)
    }
    if (address.postcode) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYouForm.setValue(zipField, address.postcode); aboutYouForm.clearErrors(zipField)
    }
    if (address.country) {
      const countryISO3 = Object.keys(ISO_3166_MAPPER).find(key => ISO_3166_MAPPER[key] === address.country)

      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYouForm.setValue(countryField, countryISO3); aboutYouForm.clearErrors(countryField)

      let payload = {}
      const purchaseItemsData = purchaseItemForm.getValues()
      if (isCoBorrower) {
        payload = {
          ...state.personal.aboutYou,
          purchaseItems: {
            itemType: selectedPurchaseItem,
            ...purchaseItemsData
          },
          coBorrowerDetails: {
            ...state.personal.aboutYou.coBorrowerDetails,
            [countryField]: countryISO3
          }
        }
      } else {
        payload = {
          ...state.personal.aboutYou,
          purchaseItems: {
            itemType: selectedPurchaseItem,
            ...purchaseItemsData
          },
          [countryField]: countryISO3
        }
      }
  
      dispatch({
        type: 'UPDATE_PERSONAL',
        step: 'aboutYou',
        payload
      })
    }
  }

  const handleCustomAddressFinderInputChange = (fieldName: string, value: string, address: Address) => {
    if (address) {
      if (fieldName === 'address') {
        updateAddressFields('', address)
      } else if (fieldName === 'previousAddress') {
        updateAddressFields('previous', address)
      } else if (fieldName === 'postAddress') {
        updateAddressFields('post', address)
      } else if (fieldName === 'coBorrowerAddress') {
        updateAddressFields('coBorrower', address, true)
      } else if (fieldName === 'coBorrowerPreviousAddress') {
        updateAddressFields('coBorrowerPrevious', address, true)
      } else if (fieldName === 'coBorrowerPostAddress') {
        updateAddressFields('coBorrowerPost', address, true)
      }
    }
  
    handleCustomInputChange(fieldName, value)
  }

  // Citzenship selection is quite particular, the form handles multiple citizenships using single variable countryOfCitizenship
  // We always have one country that needs to be selected, but we also have conditionally displayed additional citizenships
  // We crate this custom function to handle it, assumption is that we have a separate select component for each citizenship
  // and that we can track and store differences in the commas separated string by passing the index of the additional citizenship
  // TODO: Try to make it more DRY
  const handleCitzenshipChange = (fieldName: string, value: string, index: number) => {
    if (fieldName === 'countryOfCitizenship') {
      const arrayOfCitizenships = countryOfCitizenship.split(',')
      arrayOfCitizenships[index] = value
      const updatedCountryOfCitizenship = arrayOfCitizenships.join(',')
      setCountryOfCitizenship(updatedCountryOfCitizenship)
      handleCustomInputChange(fieldName, updatedCountryOfCitizenship)
    } else if (fieldName === 'coBorrowerCountryOfCitizenship') {
      const arrayOfCitizenships = countryOfCoBorrowerCitizenship.split(',')
      arrayOfCitizenships[index] = value
      const updatedCountryOfCitizenship = arrayOfCitizenships.join(',')
      setCountryOfCoBorrowerCitizenship(updatedCountryOfCitizenship)
      handleCustomInputChange(fieldName, updatedCountryOfCitizenship)
    }
  }

  console.log('errors purchase item', purchaseItemForm.formState.errors)
  console.log('errors', aboutYouForm.formState.errors)

  return (
    <>
      <Header />
      <section>
        <Heading>What are you looking to purchase?</Heading>
        <Box>
          <form onSubmit={purchaseItemForm.handleSubmit(onPurchaseItemSubmit)}>
            <PurchaseItems 
              onSelect={(value) => setSelectedPurchaseItem(value)}
              defaultValue={selectedPurchaseItem}
              completedSelectingPurchaseItem={completedSelectingPurchaseItem}
              buyingOptions={{
                icons: (dealerContext?.dealerThemeIcon?.buyingOptions || []).map((item: any) => ({
                  icon: item.icon,
                  option: item.option
                })),
                list: dealerContext?.dealerInformation?.buyingOptions || []
              }}
            />
            { !completedSelectingPurchaseItem ? (
              <div className='space-y-7'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Input
                    label='Purchase price' 
                    placeholder='Amount' 
                    inputId='purchasePrice'
                    required={true}
                    money={true}
                    {...purchaseItemForm.register('purchasePrice', { 
                      required: 'Purchase price is required',
                      validate: (value) => {
                        let loanAmount = dealerContext?.dealerInformation.minimumLoanAmount || defaultMinPersonalLoanAmount
                        if (selectedPurchaseItem === 'RM') {
                          loanAmount = 2500
                        }
                        const result = validateMoney(true, value, loanAmount)
                        if (result !== true) return result
                        return true
                      },
                      onBlur: () => purchaseItemForm.trigger('purchasePrice')
                    })}
                    error={purchaseItemForm.formState.errors.purchasePrice}
                  />
                  <Select
                    label='Loan term'
                    required={true}
                    selectOptions={loanTermOptions}
                    defaultValue={state.personal.aboutYou.purchaseItems.loanTerm}
                    onChange={value => handlePurchaseItemInputChange('loanTerm', value)}
                    error={purchaseItemForm.formState.errors.loanTerm}
                  />
                  <Select
                    label='Payments frequency'
                    required={true}
                    selectOptions={paymentFrequencyOptions}
                    defaultValue={state.personal.aboutYou.purchaseItems.repaymentFrequency}
                    onChange={value => handlePurchaseItemInputChange('repaymentFrequency', value)}
                    error={purchaseItemForm.formState.errors.repaymentFrequency}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Input
                    label='Deposit amount'
                    placeholder='Deposit'
                    inputId='cashDeposit'
                    money={true}
                    {...purchaseItemForm.register('cashDeposit', {
                      validate: (value) => validateMoney(false, value)
                    })}
                    error={purchaseItemForm.formState.errors.cashDeposit}
                  />
                  {(dealerContext?.dealerInformation?.make.length || 0) <= 1 ?
                    <Input
                      label='Make'
                      placeholder='Make'
                      inputId='make'
                      {...purchaseItemForm.register('make')}
                      error={purchaseItemForm.formState.errors.make}
                    /> :
                    <Select
                      label='Make'
                      selectOptions={dealerContext?.dealerInformation.make || []}
                      defaultValue={state.personal.aboutYou.purchaseItems.make}
                      onChange={value => handlePurchaseItemInputChange('make', value)}
                      error={purchaseItemForm.formState.errors.make}
                    />
                  }
                  {(dealerContext?.dealerInformation.model.length || 0) <= 1 ?
                    <Input
                      label='Model'
                      placeholder='Model'
                      inputId='model'
                      {...purchaseItemForm.register('model')}
                      error={purchaseItemForm.formState.errors.model}
                    /> :
                    <Select
                      label='Model'
                      selectOptions={dealerContext?.dealerInformation.model || []}
                      defaultValue={state.personal.aboutYou.purchaseItems.model}
                      onChange={value => handlePurchaseItemInputChange('model', value)}
                      error={purchaseItemForm.formState.errors.model}
                    />
                  }
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Model year'
                    required={true}
                    selectOptions={modelYearOptions}
                    defaultValue={state.personal.aboutYou.purchaseItems.year}
                    onChange={value => handlePurchaseItemInputChange('year', value)}
                    error={purchaseItemForm.formState.errors.year}
                  />
                </div>
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8 pt-12 pb-4'>
                <dl>
                  <dt className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Purchase price</dt>
                  <dd className='text-lg pl-2.5 pt-2'>${state.personal.aboutYou.purchaseItems.purchasePrice}</dd>
                </dl>
                <dl>
                  <dt className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Loan term</dt>
                  <dd className='text-lg pl-2.5 pt-2'>{state.personal.aboutYou.purchaseItems.loanTerm}</dd>
                </dl>
                <dl>
                  <dt className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Payments frequency</dt>
                  <dd className='text-lg pl-2.5 pt-2'>{state.personal.aboutYou.purchaseItems.repaymentFrequency}</dd>
                </dl>
              </div>
            )}

            <div className='flex justify-end'>
              {completedSelectingPurchaseItem && (
                <Button
                  icon='edit'
                  style='text'
                  type='button'
                  onClick={() => setCompletedSelectingPurchaseItem(false)}
                  className='-mb-4'
                >
                  Edit
                </Button>
              )}
              {!completedSelectingPurchaseItem && (
                <Button
                  type='submit'
                  className='max-md:mt-6'
                >
                  Add
                </Button>
              )}
            </div>
            <div className='flex justify-end mt-4'>
              {showPurchaseItemAddWarning && (
                <div className='text-red-500'>Please add purchase item</div>
              )}
            </div>
          </form>
        </Box>
      </section>
      <form onSubmit={async (e) => {
        // If purchase item form is valid, proceed with about you form submission
        aboutYouForm.handleSubmit(onAboutYouSubmit)(e)

        window.setTimeout(async () => {
          const isPurchaseItemValid = await purchaseItemForm.trigger()
          if (!isPurchaseItemValid) {
            // If purchase item form is not valid, scroll to the top of the page
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } else {
            if (Object.keys(aboutYouForm.formState.errors).length > 0) {
              scrollToFirstError()
            }
          }
        }, 0)
      }}>
        <section>
          <Heading as='h2'>Your personal information</Heading>
          <Box>
            <Question
              title='Is this a single or joint application? *'
              name='applicationType'
              defaultValue={state.personal.aboutYou.isJoint}
              onChange={value => handleCustomInputChange('isJoint', value)}
              error={aboutYouForm.formState.errors.isJoint}
              options={[
                { id: 'singleApplication', title: 'Single', value: false },
                { id: 'jointApplication', title: 'Joint', value: true }
              ]}
            />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select
                label='Marital status'
                required={true}
                selectOptions={martialStatusOptions}
                defaultValue={state.personal.aboutYou.maritalStatus}
                onChange={value => handleCustomInputChange('maritalStatus', value)}
                error={aboutYouForm.formState.errors.maritalStatus}
              />
              <Select
                label='How many dependants do you have?'
                selectOptions={dependantsOptions}
                defaultValue={state.personal.aboutYou.dependantsNumber}
                onChange={value => handleCustomInputChange('dependantsNumber', value)}
                error={aboutYouForm.formState.errors.dependantsNumber}
              />
            </div>
            <hr className='text-[var(--form-box-divider)]' />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select 
                label='Driver licence type'
                required={true}
                selectOptions={drivingLicenceOptions}
                defaultValue={state.personal.aboutYou.driverLicenceType}
                onChange={value => handleCustomInputChange('driverLicenceType', value)}
                error={aboutYouForm.formState.errors.driverLicenceType}
              />
              <Input
                label='Driver licence number' 
                placeholder={doesUserHaveDrivingLicence ? 'Driver licence number' : 'No driver licence number needed'}
                inputId='driversLicenceNumber'
                required={doesUserHaveDrivingLicence}
                maxLength={8}
                {...aboutYouForm.register('driverLicenceNumber', { 
                  required: doesUserHaveDrivingLicence ? 'Driver licence number is required' : false 
                })}
                disabled={!doesUserHaveDrivingLicence}
                error={aboutYouForm.formState.errors.driverLicenceNumber}
              />
              <Input 
                label='Driver licence version'
                placeholder={doesUserHaveDrivingLicence ? 'Driver licence version' : 'No driver licence version needed'}
                inputId='driversLicenceVersion'
                required={doesUserHaveDrivingLicence}
                maxLength={3}
                {...aboutYouForm.register('driverLicenceVersion', { 
                  required: doesUserHaveDrivingLicence ? 'Driver licence version is required' : false
                })}
                disabled={!doesUserHaveDrivingLicence}
                error={aboutYouForm.formState.errors.driverLicenceVersion}
              />
            </div>
          </Box>
          <Box>
            <Question
              title='Are you a New Zealand citizen or resident? *'
              name='nzResident'
              defaultValue={state.personal.aboutYou.nzResident}
              onChange={value => handleCustomInputChange('nzResident', value)}
              error={aboutYouForm.formState.errors.nzResident}
              options={[
                { id: 'nzResidentYes', title: 'Yes', value: true },
                { id: 'nzResidentNo', title: 'No', value: false }
              ]}
            />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select 
                label='Country of birth'
                required={true}
                selectOptions={listOfCountries}
                defaultValue={state.personal.aboutYou.countryOfBirth}
                onChange={value => handleCustomInputChange('countryOfBirth', value)}
                error={aboutYouForm.formState.errors.countryOfBirth}
              />
              <Select 
                label='Country of citizenship'
                required={true}
                selectOptions={listOfCountries}
                // Country of citizenship can be empty string, just one country 'ITA' or multiple countries 'ITA,AUS,ALB', the function
                // below will work in all those scenarios, here it's always our first country in the string
                defaultValue={state.personal.aboutYou.countryOfCitizenship?.split(',')[0] || ''}
                onChange={value => handleCitzenshipChange('countryOfCitizenship', value, 0)}
                error={aboutYouForm.formState.errors.countryOfCitizenship}
              />
              <div>
                <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)] max-md:hidden'>&nbsp;</span>
                <div className='relative mt-2.5'>
                  { numberOfAdditionalCitizenships < 3 && (
                    <Button icon='add' style='text' onClick={() => setNumberOfAdditionalCitizenships(numberOfAdditionalCitizenships + 1)}>
                      Add new citizenship
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {Array.from({ length: numberOfAdditionalCitizenships }).map((_, index) => (
              <div key={index} className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                <div className='max-md:hidden' />
                <Select
                  label='Country of citizenship'
                  selectOptions={listOfCountries}
                  defaultValue={state.personal.aboutYou.countryOfCitizenship.split(',')[index + 1] ?? ''}
                  onChange={value => handleCitzenshipChange('countryOfCitizenship', value, index + 1)}
                />
                <div>
                  <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)] max-md:hidden'>&nbsp;</span>
                  <div className='relative mt-2.5'>
                    <Button 
                      icon='remove'
                      style='text'
                      onClick={() => {
                        setNumberOfAdditionalCitizenships(numberOfAdditionalCitizenships - 1)
                      }}
                      type='button'
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </Box>
          <Heading as='h2'>Your address details</Heading>
          <Box>
            <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
              <div className="colspan-1">
                <AddressFinderInput 
                  label='Address' 
                  placeholder='Address' 
                  inputId='address' 
                  required={true}
                  defaultValue={state.personal.aboutYou.address}
                  onChange={(value, address) => handleCustomAddressFinderInputChange('address', value, address)}
                  error={aboutYouForm.formState.errors.address}
                />
              </div>
              <Input 
                label='City' 
                placeholder='City' 
                inputId='city'
                required={true}
                defaultValue={state.personal.aboutYou.city}
                {...aboutYouForm.register('city', { required: 'City is required' })}
                error={aboutYouForm.formState.errors.city}
              />
              <Input
                label='Postal code'
                placeholder='Postal code'
                inputId='postalCode'
                required={true}
                defaultValue={state.personal.aboutYou.ZIP}
                {...aboutYouForm.register('ZIP', { required: 'Postal code is required' })}
                error={aboutYouForm.formState.errors.ZIP}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select
                label='Country'
                required={true}
                selectOptions={listOfCountries}
                defaultValue={state.personal.aboutYou.country}
                onChange={value => handleCustomInputChange('country', value)}
                error={aboutYouForm.formState.errors.country}
              />
              <Select
                label='Residence type'
                required={true}
                selectOptions={residenceTypeOptions}
                defaultValue={state.personal.aboutYou.residenceType}
                onChange={value => handleCustomInputChange('residenceType', value)}
                error={aboutYouForm.formState.errors.residenceType}
              />
              <fieldset>
                <legend className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Same as postal address</legend>
                <Checkbox
                  label='Postal address'
                  inputId='reuseForPostalAddress' 
                  defaultChecked={state.personal.aboutYou.reuseForPostalAddress}
                  {...aboutYouForm.register('reuseForPostalAddress')}
                />
              </fieldset> 
            </div>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
              <Select
                label='Time at current address'
                selectOptions={addressYearsOptions}
                defaultValue={state.personal.aboutYou.timeAtAddressYears}
                onChange={value => handleCustomInputChange('timeAtAddressYears', value)}
                error={aboutYouForm.formState.errors.timeAtAddressYears}
              />
              <Select
                label='Months'
                hideLabel={true}
                selectOptions={addressMonthOptions}
                defaultValue={state.personal.aboutYou.timeAtAddressMonths}
                onChange={value => handleCustomInputChange('timeAtAddressMonths', value)}
                error={aboutYouForm.formState.errors.timeAtAddressMonths}
              />
            </div>
          </Box>
          
          { showPreviousAddress && (
            <>
              <Heading as='h2'>Previous address details</Heading>
              <Box>
                <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                  <div className="colspan-1">
                    <AddressFinderInput 
                      label='Address' 
                      placeholder='Address' 
                      inputId='previousAddress' 
                      required={true}
                      defaultValue={state.personal.aboutYou.previousAddress}
                      onChange={(value, address) => handleCustomAddressFinderInputChange('previousAddress', value, address)}
                      error={aboutYouForm.formState.errors.previousAddress}
                    />
                  </div>
                  <Input 
                    label='City' 
                    placeholder='City' 
                    inputId='previousCity'
                    required={true}
                    defaultValue={state.personal.aboutYou.previousCity}
                    {...aboutYouForm.register('previousCity', { required: 'City is required' })}
                    error={aboutYouForm.formState.errors.previousCity}
                  />
                  <Input
                    label='Postal code'
                    placeholder='Postal code'
                    inputId='previousZIP'
                    required={true}
                    defaultValue={state.personal.aboutYou.previousZIP}
                    {...aboutYouForm.register('previousZIP', { required: 'Postal code is required' })}
                    error={aboutYouForm.formState.errors.previousZIP}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Country'
                    required={true}
                    selectOptions={listOfCountries}
                    defaultValue={state.personal.aboutYou.previousCountry}
                    onChange={value => handleCustomInputChange('previousCountry', value)}
                    error={aboutYouForm.formState.errors.previousCountry}
                  />
                </div>
                <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Time at previous address'
                    selectOptions={addressYearsOptions}
                    defaultValue={state.personal.aboutYou.previousTimeAtAddressYears}
                    onChange={value => handleCustomInputChange('previousTimeAtAddressYears', value)}
                  />
                  <Select
                    label='Months'
                    hideLabel={true}
                    selectOptions={addressMonthOptions}
                    defaultValue={state.personal.aboutYou.previousTimeAtAddressMonths}
                    onChange={value => handleCustomInputChange('previousTimeAtAddressMonths', value)}
                  />
                </div>
              </Box>
            </>
          )}
          
          { showYourPostalAddress && (
            <>
              <Heading as='h2'>Postal address details</Heading>
              <Box>
                <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                  <div className="colspan-1">
                    <AddressFinderInput 
                      label='Address' 
                      placeholder='Address' 
                      inputId='postAddress' 
                      required={true}
                      defaultValue={state.personal.aboutYou.postAddress}
                      onChange={(value, address) => handleCustomAddressFinderInputChange('postAddress', value, address)}
                      error={aboutYouForm.formState.errors.postAddress}
                    />
                  </div>
                  <Input 
                    label='City' 
                    placeholder='City' 
                    inputId='postCity'
                    required={true}
                    defaultValue={state.personal.aboutYou.postCity}
                    {...aboutYouForm.register('postCity', { required: 'City is required' })}
                    error={aboutYouForm.formState.errors.postCity}
                  />
                  <Input
                    label='Postal code'
                    placeholder='Postal code'
                    inputId='postZIP'
                    required={true}
                    defaultValue={state.personal.aboutYou.postZIP}
                    {...aboutYouForm.register('postZIP', { required: 'Postal code is required' })}
                    error={aboutYouForm.formState.errors.postZIP}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Country'
                    required={true}
                    selectOptions={listOfCountries}
                    defaultValue={state.personal.aboutYou.postCountry}
                    onChange={value => handleCustomInputChange('postCountry', value)}
                    error={aboutYouForm.formState.errors.postCountry}
                  />
                </div>
              </Box>
            </>
          )}
        </section>

        { isJointApplication && (
        <section>
          <Heading as='h2'>Co-borrower personal details</Heading>
          <Box>
            <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
              <Select 
                label='Title'
                required={true}
                selectOptions={selectTitleOptions}
                defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerTitle || null}
                onChange={value => handleCustomInputChange('coBorrowerTitle', value)}
                error={aboutYouForm.formState.errors.coBorrowerTitle}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
              <Input
                label='First Name'
                inputId='coBorrowerFirstName'
                required={true}
                placeholder='First name'
                {...aboutYouForm.register('coBorrowerFirstName', { required: 'First name is required' })}
                error={aboutYouForm.formState.errors.coBorrowerFirstName}
              />
              <Input
                label='Middle Name'
                inputId='coBorrowerMiddleName'
                placeholder='Middle name'
                {...aboutYouForm.register('coBorrowerMiddleName')}
              />
              <Input
                label='Last Name'
                inputId='coBorrowerLastName'
                required={true}
                placeholder='Last name'
                {...aboutYouForm.register('coBorrowerLastName', { required: 'Last name is required' })}
                error={aboutYouForm.formState.errors.coBorrowerLastName}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Input 
                label='Mobile Number' 
                inputId='coBorrowerPhoneNumber' 
                required={true}
                placeholder='Mobile Number'
                {...aboutYouForm.register('coBorrowerPhoneNumber', { 
                  required: 'Mobile number is required',
                  minLength: { value: 8, message: 'Mobile number must be at least 8 digits' },
                  // TODO: come with a better phone format validation and refactor
                  pattern: {
                    value: mobileNumberRegex,
                    message: 'Invalid mobile number format'
                  }
                })}
                type='tel'
                error={aboutYouForm.formState.errors.coBorrowerPhoneNumber}
              />
              <Input 
                label='Email Address'
                inputId='coBorrowerEmail' 
                required={true}
                placeholder='Email Address'
                {...aboutYouForm.register('coBorrowerEmail', { 
                  required: 'Email address is required',
                  // TODO: come with a better email format validation and refactor
                  pattern: {
                    value: emailRegex,
                    message: 'Invalid email address format'
                  }
                })}
                type='email'
                error={aboutYouForm.formState.errors.coBorrowerEmail}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              {/* TODO: Improve and decide on the best approach */}
              <Input 
                label='Date of Birth' 
                inputId='coBorrowerDOB'
                required={true}
                {...aboutYouForm.register('coBorrowerDOB', { required: 'Date of birth is required' })}
                type='date'
                min={minDateOfBirth}
                max={maxDateOfBirth}
                error={aboutYouForm.formState.errors.coBorrowerDOB}
              />
              <Select
                label='Marital status'
                required={true}
                selectOptions={martialStatusOptions}
                defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerMaritalStatus || null}
                onChange={value => handleCustomInputChange('coBorrowerMaritalStatus', value)}
                error={aboutYouForm.formState.errors.coBorrowerMaritalStatus}
              />
              <Select 
                label='How many dependants do you have?'
                selectOptions={dependantsOptions}
                defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerDependantsNumber || null}
                onChange={value => handleCustomInputChange('coBorrowerDependantsNumber', value)}
                error={aboutYouForm.formState.errors.coBorrowerDependantsNumber}
              />
            </div>
            <hr className='text-[var(--form-box-divider)]' />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select 
                label='Driver licence type'
                required={true}
                selectOptions={drivingLicenceOptions}
                defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerDriverLicenceType || null}
                onChange={value => handleCustomInputChange('coBorrowerDriverLicenceType', value)}
                error={aboutYouForm.formState.errors.coBorrowerDriverLicenceType}
              />
              <Input
                label='Driver licence number' 
                placeholder={doesCoBorrowerHaveDrivingLicence ? 'Driver licence number' : 'No driver licence number needed'}
                inputId='driversLicenceNumber'
                required={doesCoBorrowerHaveDrivingLicence}
                maxLength={8}
                {...aboutYouForm.register('coBorrowerDriverLicenceNumber', { 
                  required: doesCoBorrowerHaveDrivingLicence ? 'Driver licence number is required' : false 
                })}
                disabled={!doesCoBorrowerHaveDrivingLicence}
                error={aboutYouForm.formState.errors.coBorrowerDriverLicenceNumber}
              />
              <Input 
                label='Driver licence version'
                placeholder={doesCoBorrowerHaveDrivingLicence ? 'Driver licence version' : 'No driver licence version needed'}
                inputId='driversLicenceVersion'
                required={doesCoBorrowerHaveDrivingLicence}
                maxLength={3}
                {...aboutYouForm.register('coBorrowerDriverLicenceVersion', { 
                  required: doesCoBorrowerHaveDrivingLicence ? 'Driver licence version is required' : false
                })}
                disabled={!doesCoBorrowerHaveDrivingLicence}
                error={aboutYouForm.formState.errors.coBorrowerDriverLicenceVersion}
              />
            </div>
          </Box>
          <Box>
            <Question
              title='Is the co-borrower a New Zealand citizen or resident? *'
              name='coBorrowerNZResident'
              defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerNZResident || null}
              onChange={value => handleCustomInputChange('coBorrowerNZResident', value)}
              error={aboutYouForm.formState.errors.coBorrowerNZResident}
              options={[
                { id: 'nzResidentCoBorrowerYes', title: 'Yes', value: true },
                { id: 'nzResidentCoBorrowerNo', title: 'No', value: false }
              ]}
            />
          </Box>
          <Box>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select 
                label='Country of birth'
                required={true}
                selectOptions={listOfCountries}
                defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountryOfBirth || ''}
                onChange={value => handleCustomInputChange('coBorrowerCountryOfBirth', value)}
                error={aboutYouForm.formState.errors.coBorrowerCountryOfBirth}
              />
              <Select 
                label='Country of citizenship'
                required={true}
                selectOptions={listOfCountries}
                defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountryOfCitizenship ? state.personal.aboutYou.coBorrowerDetails.coBorrowerCountryOfCitizenship.split(',')[0] : ''}
                onChange={value => handleCitzenshipChange('coBorrowerCountryOfCitizenship', value, 0)}
                error={aboutYouForm.formState.errors.coBorrowerCountryOfCitizenship}
              />
              <div>
                <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)] max-md:hidden'>&nbsp;</span>
                <div className='relative mt-2.5'>
                  { numberOfCoBorrowerAdditionalCitizenships < 3 && (
                    <Button icon='add' style='text' onClick={() => setNumberOfCoBorrowerAdditionalCitizenships(numberOfCoBorrowerAdditionalCitizenships + 1)}>
                      Add new citizenship
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {Array.from({ length: numberOfCoBorrowerAdditionalCitizenships }).map((_, index) => (
              <div key={index} className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                <div className='max-md:hidden' />
                <Select
                  label='Country of citizenship'
                  selectOptions={listOfCountries}
                  defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountryOfCitizenship ? state.personal.aboutYou.coBorrowerDetails.coBorrowerCountryOfCitizenship.split(',')[index + 1] : ''}
                  onChange={value => handleCitzenshipChange('coBorrowerCountryOfCitizenship', value, index + 1)}
                />
                <div>
                  <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)] max-md:hidden'>&nbsp;</span>
                  <div className='relative mt-2.5'>
                    <Button 
                      icon='remove'
                      style='text' 
                      onClick={() => setNumberOfCoBorrowerAdditionalCitizenships(numberOfCoBorrowerAdditionalCitizenships - 1)}
                      type='button'
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </Box>

          <Box>
            <Question
              title='Do you live together in the same address? *'
              name='coBorrowerLivingTogether'
              defaultValue={state.personal.aboutYou.coBorrowerDetails?.isCoBorrowerLivingTogether || null}
              onChange={value => handleCustomInputChange('isCoBorrowerLivingTogether', value)}
              error={aboutYouForm.formState.errors.isCoBorrowerLivingTogether}
              options={[
                { id: 'liveTogetherBorrowerYes', title: 'Yes', value: true },
                { id: 'liveTogetherCoBorrowerNo', title: 'No', value: false }
              ]}
            />
          </Box>
          { showCoBorrowerAddress && (
            <>
              <Heading as='h2'>Co-borrowers address details</Heading>
              <Box>
                <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                  <div className="colspan-1">
                    <AddressFinderInput 
                      label='Address' 
                      placeholder='Address' 
                      inputId='coBorrowerAddress' 
                      required={true}
                      defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerAddress || ''}
                      onChange={(value, address) => handleCustomAddressFinderInputChange('coBorrowerAddress', value, address)}
                      error={aboutYouForm.formState.errors.coBorrowerAddress}
                    />
                  </div>
                  <Input 
                    label='City' 
                    placeholder='City' 
                    inputId='coBorrowerCity'
                    required={true}
                    defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerCity || ''}
                    {...aboutYouForm.register('coBorrowerCity', { required: 'City is required' })}
                    error={aboutYouForm.formState.errors.coBorrowerCity}
                  />
                  <Input
                    label='Postal code'
                    placeholder='Postal code'
                    inputId='coBorrowerZIP'
                    required={true}
                    defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerZIP || ''}
                    {...aboutYouForm.register('coBorrowerZIP', { required: 'Postal code is required' })}
                    error={aboutYouForm.formState.errors.coBorrowerZIP}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Country'
                    required={true}
                    selectOptions={listOfCountries}
                    defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerCountry || ''}
                    onChange={value => handleCustomInputChange('coBorrowerCountry', value)}
                    error={aboutYouForm.formState.errors.coBorrowerCountry}
                  />
                  <Select
                    label='Residence type'
                    required={true}
                    selectOptions={residenceTypeOptions}
                    defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerResidenceType || ''}
                    onChange={value => handleCustomInputChange('coBorrowerResidenceType', value)}
                    error={aboutYouForm.formState.errors.coBorrowerResidenceType}
                  />
                  <fieldset>
                    <legend className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Same as postal address</legend>
                    <Checkbox
                      label='Postal address'
                      inputId='coBorrowerReuseForPostalAddress' 
                      defaultChecked={state.personal.aboutYou.coBorrowerDetails?.coBorrowerReuseForPostalAddress || false}
                      {...aboutYouForm.register('coBorrowerReuseForPostalAddress')}
                    />
                  </fieldset>
                </div>
                <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Time at current address'
                    selectOptions={addressYearsOptions}
                    defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerTimeAtAddressYears || null}
                    onChange={value => handleCustomInputChange('coBorrowerTimeAtAddressYears', value)}
                  />
                  <Select
                    label='Months'
                    hideLabel={true}
                    selectOptions={addressMonthOptions}
                    defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerTimeAtAddressMonths || null}
                    onChange={value => handleCustomInputChange('coBorrowerTimeAtAddressMonths', value)}
                  />
                </div>
              </Box>
              
              { showCoBorrowerPreviousAddress && (
                <>
                  <Heading as='h2'>Previous address details</Heading>
                  <Box>
                    <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                      <div className="colspan-1">
                        <AddressFinderInput 
                          label='Address' 
                          placeholder='Address' 
                          inputId='coBorrowerPreviousAddress' 
                          required={true}
                          defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousAddress || ''}
                          onChange={(value, address) => handleCustomAddressFinderInputChange('coBorrowerPreviousAddress', value, address)}
                          error={aboutYouForm.formState.errors.coBorrowerPreviousAddress}
                        />
                      </div>
                      <Input 
                        label='City' 
                        placeholder='City' 
                        inputId='coBorrowerPreviousCity'
                        required={true}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousCity || ''}
                        {...aboutYouForm.register('coBorrowerPreviousCity', { required: 'City is required' })}
                        error={aboutYouForm.formState.errors.coBorrowerPreviousCity}
                      />
                      <Input
                        label='Postal code'
                        placeholder='Postal code'
                        inputId='coBorrowerPreviousZIP'
                        required={true}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousZIP || ''}
                        {...aboutYouForm.register('coBorrowerPreviousZIP', { required: 'Postal code is required' })}
                        error={aboutYouForm.formState.errors.coBorrowerPreviousZIP}
                      />
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                      <Select
                        label='Country'
                        required={true}
                        selectOptions={listOfCountries}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousCountry || ''}
                        onChange={value => handleCustomInputChange('coBorrowerPreviousCountry', value)}
                        error={aboutYouForm.formState.errors.coBorrowerPreviousCountry}
                      />
                    </div>
                    <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                      <Select
                        label='Time at previous address'
                        selectOptions={addressYearsOptions}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousTimeAtAddressYears || null}
                        onChange={value => handleCustomInputChange('coBorrowerPreviousTimeAtAddressYears', value)}
                      />
                      <Select
                        label='Months'
                        hideLabel={true}
                        selectOptions={addressMonthOptions}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPreviousTimeAtAddressMonths || null}
                        onChange={value => handleCustomInputChange('coBorrowerPreviousTimeAtAddressMonths', value)}
                      />
                    </div>
                  </Box>         
                </>
              )}

              { showCoBorrowerPostalAddress && (
                <>
                  <Heading as='h2'>Co-borrower postal address details</Heading>
                  <Box>
                    <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                      <div className="colspan-1">
                        <AddressFinderInput 
                          label='Address' 
                          placeholder='Address' 
                          inputId='coBorrowerPostAddress' 
                          required={true}
                          defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostAddress || ''}
                          onChange={(value, address) => handleCustomAddressFinderInputChange('coBorrowerPostAddress', value, address)}
                          error={aboutYouForm.formState.errors.coBorrowerPostAddress}
                        />
                      </div>
                      <Input 
                        label='City' 
                        placeholder='City' 
                        inputId='coBorrowerPostCity'
                        required={true}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostCity || ''}
                        {...aboutYouForm.register('coBorrowerPostCity', { required: 'City is required' })}
                        error={aboutYouForm.formState.errors.coBorrowerPostCity}
                      />
                      <Input
                        label='Postal code'
                        placeholder='Postal code'
                        inputId='coBorrowerPostZIP'
                        required={true}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostZIP || ''}
                        {...aboutYouForm.register('coBorrowerPostZIP', { required: 'Postal code is required' })}
                        error={aboutYouForm.formState.errors.coBorrowerPostZIP}
                      />
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                      <Select
                        label='Country'
                        required={true}
                        selectOptions={listOfCountries}
                        defaultValue={state.personal.aboutYou.coBorrowerDetails?.coBorrowerPostCountry || ''}
                        onChange={value => handleCustomInputChange('coBorrowerPostCountry', value)}
                        error={aboutYouForm.formState.errors.coBorrowerPostCountry}
                      />
                    </div>
                  </Box>
                </>
              )}
            </>
          )}
          </section>
        )}
        {state.isPostCall && (
          <p className="text-center text-gray-600 mt-6 max-w-2xl mx-auto">By clicking "Next" you are confirming that the above information is correct</p>
        )}
        <div className='flex justify-between mt-8 pb-16 max-w-5xl mx-auto'>
          <Button iconPosition='left' onClick={() => navigate(ROUTES.PERSONAL.YOUR_DETAILS.path)}>Back</Button>
          <Button type='submit'>Next</Button>
        </div>
      </form>
    </>
  )
}

export default AboutYou
